import { toRaw } from "vue";

/**
 * Strip Vue reactive proxies at every depth of arrays and plain objects.
 *
 * IndexedDB structured-clones what it stores and throws a DataCloneError on
 * proxies. `toRaw` alone only unwraps the outer value, while documents built
 * from reactive state (spreads, `map`) can still hold proxies deeper down.
 * Other objects (Date, class instances) are returned unwrapped as they are.
 */
export function toRawDeep<T>(value: T): T {
  const raw = toRaw(value);

  if (Array.isArray(raw)) {
    return raw.map((item: unknown) => toRawDeep(item)) as T;
  }

  if (raw !== null && typeof raw === "object" && isPlainObject(raw)) {
    return Object.fromEntries(
      Object.entries(raw).map(([key, item]) => [key, toRawDeep(item)]),
    ) as T;
  }

  return raw;
}

function isPlainObject(value: object): boolean {
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
